import { Link } from 'react-router-dom'
import type { ProductSummary } from '../api/types'
import { useCart } from '../cart'
import { useT } from '../i18n'
import { usePriceFormat } from '../shopConfig'
import { useMeldung } from './Meldung'
import { Zeichen } from './Zeichen'

/**
 * Eine Produktkachel.
 *
 * Das Produkt steht auf einer ruhigen, gleichförmigen Bühne (Klarna, Shop,
 * Selfridges): Händlerfotos haben jeden erdenklichen Hintergrund, und auf einer
 * gemeinsamen Fläche wird aus ihnen ein Raster statt eines Flickenteppichs.
 *
 * „ab" steht vor dem Preis, wenn es mehrere Varianten gibt — priceFrom ist der
 * niedrigste über alle. Ohne das Wort stünde in der Liste ein anderer Preis als
 * auf der Produktseite, sobald die Größen verschieden kosten. Bei genau einer
 * Variante (quickAddVariantId) gibt es nur den einen Preis, und das „ab" wäre
 * eine falsche Vorsicht.
 */
function Kachel({ produkt }: { produkt: ProductSummary }) {
  const t = useT()
  const preis = usePriceFormat()
  const einzeln = Boolean(produkt.quickAddVariantId)

  return (
    <li className="sf-kachel" data-ausverkauft={produkt.available ? 'nein' : 'ja'}>
      <Link to={`/produkt/${produkt.handle}`}>
        <div className="sf-kachel-buehne">
          {produkt.imageUrl ? (
            <>
              {/* Der Produktname als alt: Er beschreibt, was zu sehen ist,
                  und ist das Einzige, was hier verlässlich stimmt. */}
              <img
                className="sf-kachel-bild"
                src={produkt.imageUrl}
                alt={produkt.name}
                loading="lazy"
                decoding="async"
              />
              {produkt.hoverImageUrl && (
                /* Das zweite Bild ist Zugabe fürs Auge — für den Vorleser ist
                   es dasselbe Produkt. */
                <img
                  className="sf-kachel-bild sf-kachel-bild-zweit"
                  src={produkt.hoverImageUrl}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </>
          ) : (
            <span
              className="sf-kachel-leer"
              role="img"
              aria-label={t('kachel.keinBildZu', { name: produkt.name })}
            >
              {produkt.name.trim().charAt(0).toUpperCase()}
            </span>
          )}

          {!produkt.available && <span className="sf-kachel-marke">{t('kachel.ausverkauftMarke')}</span>}
        </div>

        <div className="sf-kachel-text">
          <p className="sf-kachel-name">{produkt.name}</p>
          <p className="sf-kachel-preis">
            {einzeln ? preis(produkt.priceFrom) : t('kachel.ab', { preis: preis(produkt.priceFrom) })}
          </p>
        </div>
      </Link>

      {produkt.quickAddVariantId && (
        <Schnellknopf variantId={produkt.quickAddVariantId} name={produkt.name} />
      )}
    </li>
  )
}

/**
 * Der schnelle Griff in den Korb — nur bei genau einer Variante.
 *
 * Eine Meldung statt der Schublade: Wer aus dem Raster heraus in den Korb legt,
 * will weiter stöbern. Die Schublade klappte ihm den Laden zu, bei jedem
 * einzelnen Stück.
 */
function Schnellknopf({ variantId, name }: { variantId: string; name: string }) {
  const t = useT()
  const { hinzufuegen, arbeitet } = useCart()
  const melden = useMeldung()

  return (
    <button
      className="sf-schnell"
      type="button"
      disabled={arbeitet}
      aria-label={t('kachel.inDenKorb', { name })}
      onClick={() => {
        void hinzufuegen(variantId).then((geklappt) =>
          melden(geklappt ? t('kachel.gelegt', { name }) : t('kachel.nichtGelegt', { name })),
        )
      }}
    >
      <Zeichen art="plus" groesse={20} />
    </button>
  )
}

export function ProductGrid({ produkte }: { produkte: ProductSummary[] }) {
  return (
    <ul className="sf-raster">
      {produkte.map((produkt) => (
        <Kachel key={produkt.id} produkt={produkt} />
      ))}
    </ul>
  )
}
